import type { ComboboxItem } from '@mantine/core';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { ExtendedInitiative } from '../../../api/initiative-client/initiative-client.type';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import { sanitizeString } from '../../../helpers/string-helpers/string-helpers';
import type { FilterControls } from '../../../store/strategy-store/strategy-store.type';
import { EPIC_SEARCH_THRESHOLD } from './initiative-performance.constants';
import { EpicSortOption, SortDirection } from './initiative-performance.type';

/**
 * Returns the cumulative completed-task count at the most recent
 * past-or-current month with data.
 *
 * Skips future-month keys because the backend injects forecasted values into
 * `completed_subtasks_over_time` so the Throughput chart can render its
 * dashed projection — those are predictions, not actual completions, and must
 * not surface as the "Tasks Completed / All Time" KPI. Walks newest-to-oldest
 * so a `null` at the current month falls back to the prior cumulative value.
 * The past/future split mirrors the chart's
 * `dayjs(date).isAfter(dayjs(), 'month')` convention to keep the tile and the
 * chart's solid green segment in lockstep.
 *
 * @param {ExtendedInitiative | null} initiativePerformance - Initiative
 *   payload from the strategy store; `null` while the request is in flight
 * @returns {number} Cumulative completed tasks at the latest historical
 *   month, or `0` when no historical data exists
 */
const getCompletedTasksCount = (initiativePerformance: ExtendedInitiative | null): number => {
  if (!initiativePerformance) {
    return 0;
  }

  const tasks = initiativePerformance?.completed_subtasks_over_time?.tasks || {};
  const monthKeys = Object.keys(tasks).sort();
  const currentDate = dayjs();

  for (let i = monthKeys.length - 1; i >= 0; i -= 1) {
    const key = monthKeys[i];
    if (dayjs(key, 'YYYY-MM').isAfter(currentDate, 'month')) {
      continue;
    }
    const value = tasks[key as keyof typeof tasks];
    if (value !== null && value !== undefined) {
      return value;
    }
  }

  return 0;
};

/**
 * Returns an array of sort options for the Epics filter
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {ComboboxItem[]} Array of sort options with value and label properties
 */
const getSortOptions = (t: TFunction): ComboboxItem[] => {
  return [
    { value: EpicSortOption.ID, label: t('strategy.initiatives.table.id') },
    { value: EpicSortOption.Name, label: t('strategy.initiatives.table.name') },
    {
      value: EpicSortOption.Tasks,
      label: t('strategy.initiatives.table.tasks', { work_units: t('common.work_units') }),
    },
  ];
};

/**
 * Returns an array of filter options for the Epics filter
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {ComboboxItem[]} Array of filter options with value and label properties
 */
const getFilterOptions = (t: TFunction): ComboboxItem[] => {
  return [{ value: 'hideEmpty', label: t('strategy.initiatives.labels.hide_empty_epics') }];
};

/**
 * Checks if a text matches a search term (case-insensitive)
 *
 * @param {string} text - The text to check
 * @param {string} searchTerm - The search term
 * @returns {boolean} True if the text matches the search term, false otherwise
 */
const textMatch = (text: string, searchTerm: string): boolean => {
  if (!searchTerm || !text) return false;

  return text.toLowerCase().includes(searchTerm.toLowerCase());
};

/**
 * Filters epics by search term (case-insensitive)
 *
 * @param {Epic[]} epics - The array of epics to filter
 * @param {string} searchTerm - The search term
 * @returns {Epic[]} Array of epics that match the search term
 */
const filterEpicsBySearch = (epics: Epic[], searchTerm: string): Epic[] => {
  if (!searchTerm) return epics;

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  return epics.filter(
    (epic) =>
      (epic.name && textMatch(epic.name, normalizedSearchTerm)) ||
      (epic.title && textMatch(epic.title, normalizedSearchTerm)),
  );
};

/**
 * Applies filters to epics
 *
 * @param {Epic[]} epics - The array of epics to filter
 * @param {Object} filters - The filters to apply
 * @returns {Epic[]} Array of epics that match the filters
 */
const applyFilters = (
  epics: Epic[],
  {
    hideEmpty,
  }: {
    hideEmpty: boolean;
  },
): Epic[] => {
  let result = [...epics];

  // Apply hide empty filter (epics with no subtasks)
  if (hideEmpty) {
    result = result.filter((epic) => epic.subtasks_count > 0);
  }

  return result;
};

/**
 * Sorts epics based on the specified sort field and direction
 *
 * @param {Epic[]} epics - The array of epics to sort
 * @param {EpicSortOption} sortField - The field to sort by (ID, Name, Tasks)
 * @param {SortDirection} sortDirection - The direction to sort (Ascending or Descending)
 * @returns {Epic[]} Sorted array of epics
 */
const sortEpics = (epics: Epic[], sortField: EpicSortOption, sortDirection: SortDirection): Epic[] => {
  return [...epics].sort((a, b) => {
    const multiplier = sortDirection === SortDirection.Asc ? 1 : -1;

    switch (sortField) {
      case EpicSortOption.ID:
        return multiplier * a.title.localeCompare(b.title);

      case EpicSortOption.Name:
        return multiplier * sanitizeString(a.name).localeCompare(sanitizeString(b.name));

      case EpicSortOption.Tasks:
        return multiplier * (a.subtasks_count - b.subtasks_count);

      default:
        return multiplier * a.title.localeCompare(b.title);
    }
  });
};

/**
 * Determines if epic search functionality should be enabled based on the number of epics
 *
 * @param {Epic[]} epics - The array of epics to check
 * @returns {boolean} True if epic search should be enabled, false otherwise
 */
const isEnabledEpicSearch = (epics: Epic[] = []): boolean => {
  if (!epics || epics.length === 0) {
    return false;
  }

  return epics.length >= EPIC_SEARCH_THRESHOLD;
};

/**
 * Filters and sorts epics based on the provided filter controls
 *
 * @param {Epic[]} epics - The array of epics to filter and sort
 * @param {FilterControls} controls - The filter controls containing search, sort and filter options
 * @param {string} controls.searchTerm - The search term to filter epics by
 * @param {EpicSortOption} controls.sortBy - The field to sort epics by
 * @param {SortDirection} controls.sortDirection - The direction to sort epics
 * @param {boolean} controls.hideEmpty - Whether to hide epics with no subtasks
 * @returns {Epic[]} Filtered and sorted array of epics
 */
const searchEpics = (epics: Epic[], controls: FilterControls): Epic[] => {
  const { searchTerm, sortBy, sortDirection, hideEmpty } = controls;

  // Step 1: Apply search filter with case-insensitive text matching
  const searchFiltered = filterEpicsBySearch(epics, searchTerm);

  // Step 2: Apply other filters
  const filtered = applyFilters(searchFiltered, { hideEmpty });

  // Step 3: Sort the filtered results
  const sorted = sortEpics(filtered, sortBy, sortDirection);

  return sorted;
};

/**
 * Checks if an array contains data
 *
 * @template T - The type of elements in the array
 * @param {T[]} data - The array to check
 * @returns {boolean} True if the array exists and has elements, false otherwise
 */
const hasArrayData = <T>(data: T[]): boolean => {
  return Array.isArray(data) && data.length > 0;
};

export {
  applyFilters,
  filterEpicsBySearch,
  getCompletedTasksCount,
  getFilterOptions,
  getSortOptions,
  hasArrayData,
  isEnabledEpicSearch,
  searchEpics,
  sortEpics,
};
